/*
 * LienzoMoments — selector de formato, tamano y paquete
 * ---------------------------------------------------------------------------
 * El HTML ya viene con todas las cards pintadas y con sus precios, mensuales y
 * ahorros calculados en Liquid. Este script NO renderiza nada: solo decide
 * que grupo de cards se ve, escribe el variant id en el formulario de
 * producto y avisa al resto de la pagina.
 *
 * Eso es deliberado. Repintar las cards en JS obligaria a duplicar aqui la
 * aritmetica de MSI y de ahorros que ya vive en el snippet de Liquid, y dos
 * copias de una regla de negocio se desincronizan siempre.
 *
 * Formatos: si el producto tiene la opcion, Liquid pinta un paso de formato
 * y cada radio de tamano lleva el suyo en `data-lm-format`. Elegir formato
 * muestra solo sus tamanos. Sin la opcion no hay radios de formato y el
 * script se comporta exactamente como antes.
 *
 * Evento que emite:
 *   lm:variant  detail: { variantId, price, format, size, package,
 *                         photoCount, photoLabels, available }
 *   Lo consumen el bloque de upload (cuantos campos de foto mostrar) y, mas
 *   adelante, la sticky bar.
 *
 * Evento que escucha:
 *   lm:select   detail: { size, package }
 *   Lo usara la seccion de combos para preseleccionar desde otro punto de la
 *   pagina. Ambos campos son opcionales.
 */
(() => {
  'use strict';

  const init = (root) => {
    if (root.dataset.lmReady === 'true') return;
    root.dataset.lmReady = 'true';

    const formatInputs = Array.from(root.querySelectorAll('[data-lm-format-input]'));
    const sizeInputs = Array.from(root.querySelectorAll('[data-lm-size-input]'));
    const cards = Array.from(root.querySelectorAll('[data-lm-tier]'));
    if (!sizeInputs.length || !cards.length) return;

    const formId = root.dataset.lmForm;

    /**
     * El input oculto que viaja en el submit. Horizon lo renderiza dentro del
     * bloque buy-buttons con ref="variantId" (blocks/buy-buttons.liquid:64).
     * Se resuelve en cada uso y no se cachea: el formulario se re-renderiza
     * cuando Horizon reemplaza la seccion, y una referencia vieja apuntaria a
     * un nodo que ya no esta en el documento.
     */
    const variantIdInput = () => {
      const form = formId ? document.getElementById(formId) : null;
      return (
        form?.querySelector('input[name="id"]') ??
        document.querySelector('product-form-component form input[name="id"]')
      );
    };

    const cardsFor = (size) => cards.filter((card) => card.dataset.lmSize === size);
    const inputOf = (card) => card.querySelector('.lm-tier__input');

    const selectedSize = () => sizeInputs.find((input) => input.checked)?.value ?? sizeInputs[0].value;

    const selectedFormat = () => formatInputs.find((input) => input.checked)?.value ?? null;

    /** Formato al que pertenece un tamano, o null si el producto no tiene formatos. */
    const formatOf = (size) => sizeInputs.find((input) => input.value === size)?.dataset.lmFormat ?? null;

    /** Un tamano tiene al menos una card comprable. */
    const hasStock = (size) =>
      cardsFor(size).some((card) => !card.classList.contains('lm-tier--unavailable'));

    /**
     * Muestra los tamanos del formato pedido y oculta y deshabilita el resto,
     * por la misma razon que las cards: un radio invisible pero habilitado
     * se alcanza con las flechas del teclado.
     */
    const showFormatSizes = (format) => {
      if (!formatInputs.length) return;
      formatInputs.forEach((input) => {
        input.checked = input.value === format;
      });
      sizeInputs.forEach((input) => {
        const inFormat = input.dataset.lmFormat === format;
        const label = input.closest('.lm-size');
        if (label) label.hidden = !inFormat;
        input.disabled = !inFormat;
        if (!inFormat) input.checked = false;
      });
    };

    /**
     * Selecciona un tamano, cambiando antes de formato si hace falta. Es la
     * unica puerta de entrada para cambiar de tamano desde fuera del paso de
     * tamano (formato, combos, preseleccion).
     */
    const selectSize = (size, preferredPackage) => {
      const format = formatOf(size);
      if (format && format !== selectedFormat()) showFormatSizes(format);
      const sizeInput = sizeInputs.find((input) => input.value === size);
      if (sizeInput) sizeInput.checked = true;
      showSize(size, preferredPackage);
    };

    /** Paquete elegido ahora mismo, para intentar conservarlo al cambiar de tamano. */
    const selectedPackage = () => {
      const checked = cards.map(inputOf).find((input) => input && input.checked && !input.disabled);
      return checked ? checked.value : null;
    };

    const announce = (input) => {
      if (!input) return;

      const idInput = variantIdInput();
      if (idInput) {
        idInput.value = input.dataset.lmVariantId;
        // Horizon escucha cambios del input para su propio estado interno.
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const labels = input.dataset.lmPhotoLabels;

      document.dispatchEvent(
        new CustomEvent('lm:variant', {
          detail: {
            variantId: input.dataset.lmVariantId,
            price: Number(input.dataset.lmPrice),
            format: input.closest('[data-lm-tier]')?.dataset.lmFormat ?? null,
            size: input.closest('[data-lm-tier]')?.dataset.lmSize ?? null,
            package: input.value,
            photoCount: Number(input.dataset.lmPhotos) || 1,
            photoLabels: labels ? labels.split('|') : [],
            available: !input.disabled,
          },
        })
      );
    };

    /**
     * Muestra el grupo del tamano pedido y deshabilita los radios de los
     * grupos ocultos. Deshabilitarlos importa: un radio invisible pero
     * habilitado sigue siendo alcanzable con teclado, y el usuario acabaria
     * seleccionando una variante que no puede ver.
     */
    const showSize = (size, preferredPackage) => {
      cards.forEach((card) => {
        const isActive = card.dataset.lmSize === size;
        const input = inputOf(card);
        card.hidden = !isActive;
        if (input) {
          // No se rehabilita lo que Liquid marco agotado.
          const soldOut = card.classList.contains('lm-tier--unavailable');
          input.disabled = !isActive || soldOut;
          if (!isActive) input.checked = false;
        }
      });

      const group = cardsFor(size).map(inputOf).filter((input) => input && !input.disabled);
      if (!group.length) return;

      const keep = preferredPackage && group.find((input) => input.value === preferredPackage);
      const target = keep ?? group[0];
      target.checked = true;
      syncSelectedClass();
      announce(target);
    };

    const syncSelectedClass = () => {
      cards.forEach((card) => {
        const input = inputOf(card);
        card.classList.toggle('is-selected', Boolean(input && input.checked));
      });
    };

    /*
     * Cambio de formato: se queda con el primer tamano del formato que tenga
     * algo comprable. El orden es el de Admin, que va de chico a grande, asi
     * que el cliente aterriza en el precio de entrada del formato. El paquete
     * (Individual, Duo, Trio) se conserva si el tamano nuevo lo ofrece.
     */
    formatInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        const sizes = sizeInputs.filter((s) => s.dataset.lmFormat === input.value).map((s) => s.value);
        const target = sizes.find(hasStock) ?? sizes[0];
        if (!target) return;
        const pkg = selectedPackage();
        showFormatSizes(input.value);
        selectSize(target, pkg);
      });
    });

    sizeInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        showSize(input.value, selectedPackage());
      });
    });

    cards.forEach((card) => {
      const input = inputOf(card);
      if (!input) return;
      input.addEventListener('change', () => {
        if (!input.checked) return;
        syncSelectedClass();
        announce(input);
      });
    });

    /** Primer tamano cuyo grupo contiene ese paquete. */
    const sizeOffering = (pkg) => {
      const card = cards.find((c) => {
        const input = inputOf(c);
        return input && input.value === pkg;
      });
      return card ? card.dataset.lmSize : null;
    };

    // Preseleccion desde otras secciones (la de combos, en el Bloque 3).
    document.addEventListener('lm:select', (event) => {
      const { size, package: pkg } = event.detail ?? {};

      if (size && sizeInputs.some((input) => input.value === size)) {
        selectSize(size, pkg ?? selectedPackage());
        return;
      }

      if (!pkg) return;

      /*
       * Piden un paquete sin decir tamano ("Elegir Trio" desde la seccion de
       * combos). Se conserva el tamano actual SI ese paquete existe ahi; si
       * no, se salta al primero que lo ofrezca.
       *
       * Sin esto, pedir "Trio" con el tab de combos activo no encontraba la
       * card, caia en la primera del grupo y seleccionaba un combo que nadie
       * habia pedido.
       */
      const current = selectedSize();
      const currentHasIt = cardsFor(current).some((c) => inputOf(c)?.value === pkg);
      const target = currentHasIt ? current : sizeOffering(pkg);
      if (!target) return;

      selectSize(target, pkg);
    });

    // Estado inicial: respeta lo que Liquid dejo marcado.
    showSize(selectedSize(), selectedPackage());
  };

  const boot = () => document.querySelectorAll('[data-lm-selector]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Horizon reemplaza secciones enteras al editar en el theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
